<script>
import DialogManager from '../../../services/DialogManager';
import ModalManager from '../../../services/modalManager';
import updateQueryStringParameter from '../../../services/updateQueryStringParameter';
import Form from '../../Form.vue';
import axios from 'axios';
import moment from 'moment';
import merge from 'deepmerge';
import i18n from '../../../i18n';
import MatchedPatients from '../../MatchedPatients.vue';
import HandlesPersonLookupAutofill from '../../../mixins/Patient/HandlesPersonLookupAutofill';

const COMPLETE_BSN_PATTERN = /^\d{9}$/;
const EXISTING_BSN_SEARCH_DEBOUNCE_MS = 400;
const DUPLICATE_BSN_EXEMPT = '000000000';

export default {
    extends: Form,

    mixins: [HandlesPersonLookupAutofill],

    props: {
        currentDate: {
            type: String,
            required: true,
        },

        countryNetherlandsId: {
            type: String,
            required: true,
        },

        personLookupUrl: {
            type: String,
            required: true,
        },

        personLookupPoliciesUrl: {
            type: String,
            required: true,
        },

        personLookupSimulationStoreUrl: {
            type: String,
            required: true,
        },

        personLookupSimulation: {
            type: Object,
            default: () => ({ available: false }),
        },

        personLookupVecozo: {
            type: Boolean,
            default: false,
        },

        patientMatchUrl: {
            type: String,
            required: true,
        },

        personLookupEnabled: {
            type: Boolean,
            default: false,
        },

        matchedPatients: {
            type: Array,
            default: () => [],
        },

        mergePatientsUrl: {
            type: String,
            default: null,
        },

        patientEditUrl: {
            type: String,
            default: null,
        },

        patientDuplicateId: {
            type: String,
            default: null,
        },

        modalContext: {
            type: Boolean,
            default: false,
        },

        isNewPatient: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            initialFinancialRelationNumber: null,
            personLookupPolicies: [],
            isPersonLookupPoliciesRefreshActive: false,
            hasAvailableAdditionalReimbursement: null,
            isPersonLookupMatchBlocking: false,
            personLookupMatchedPatient: null,
            existingBsnPatient: null,
            existingBsnSearchDebounce: null,
            existingBsnSearchedFor: null,
            existingBsnSearchSource: null,
            isSearchingExistingBsnPatient: false,
            identificationNumberBeforeNoBsn: null,
        };
    },

    mounted() {
        this.initialFinancialRelationNumber = this.formData.patient_financial.financial_relation_number;

        if (this.shouldCheckExistingBsn(this.formData.identification_number)) {
            this.searchExistingBsnPatient(String(this.formData.identification_number));
        }

        if (this.matchedPatients.length) {
            this.showMatchedPatientsModal();
        } else if (this.patientDuplicateId) {
            this.dismissStalePatientDuplicate();
        }
    },

    beforeDestroy() {
        this.clearExistingBsnSearch();
    },

    computed: {
        hasNoBsn: {
            get() {
                return String(this.formData.identification_number || '') === DUPLICATE_BSN_EXEMPT;
            },

            set(hasNoBsn) {
                if (hasNoBsn) {
                    this.identificationNumberBeforeNoBsn = this.formData.identification_number || '';
                    this.formData.identification_number = DUPLICATE_BSN_EXEMPT;

                    return;
                }

                this.formData.identification_number = this.identificationNumberBeforeNoBsn ?? '';
            },
        },

        age() {
            const { birthdate, deceased } = this.formData;

            if (!birthdate) {
                return null;
            }

            const start = new Date(birthdate);
            const end = new Date(deceased ? this.formData.deceased_date : this.currentDate);

            if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
                return null;
            }

            const duration = moment.duration(moment(end).diff(start));

            const years = duration.years();
            const months = duration.months();

            const yearText = i18n.t('patient.age.terms.year');
            const monthText = months === 1 ? i18n.t('patient.age.terms.month') : i18n.t('patient.age.terms.months');

            if (years < 2) {
                return `${years} ${yearText} ${months} ${monthText}`;
            } else {
                return `${years} ${yearText}`;
            }
        },

        countryIsNetherlands() {
            return String(this.formData.address.country_id) === this.countryNetherlandsId;
        },

        billingCountryIsNetherlands() {
            return String(this.formData.billing_address.country_id) === this.countryNetherlandsId;
        },

        enableRequestPersonalData() {
            return Boolean(this.formData.identification_number) ||
                Boolean(this.formData.name.lastname && this.formData.address.postal_code && this.formData.address.house_number && this.formData.gender && this.formData.birthdate);
        },

        enableBsnVerification() {
            return this.formData.identification_number && this.formData.name.lastname && this.formData.birthdate && this.formData.gender;
        },

        enableIdentificationDataVerification() {
            return this.formData.identification_number && this.formData.identification_data.document_type && this.formData.identification_data.document_number;
        },

        showFinancialRelationNumberChangedWarning() {
            if (!this.initialFinancialRelationNumber) {
                return false;
            }

            return this.initialFinancialRelationNumber !== this.formData.patient_financial.financial_relation_number;
        },

        showAdditionalReimbursementWarning() {
            return this.hasAvailableAdditionalReimbursement === false &&
                !this.isPersonLookupPoliciesRefreshActive;
        },

        hasPersonLookupPolicies() {
            return this.personLookupPolicies.length > 0;
        },

        existingBsnPatientEditUrl() {
            if (!this.existingBsnPatient) {
                return null;
            }

            return this.patientEditUrl.replace('__PATIENT_ID__', this.existingBsnPatient.id);
        },

        existingBsnPatientName() {
            return this.existingBsnPatient?.name?.fullname || '';
        },

        submitButtonEnabled() {
            return (!this.$store.state.core.requestPoolPending || this.isPersonLookupPoliciesRefreshActive) &&
                this.existingBsnSearchDebounce === null &&
                !this.isSearchingExistingBsnPatient &&
                !this.isPersonLookupMatchBlocking &&
                !this.formSubmitted;
        },
    },

    watch: {
        'formData.identification_number'(bsn) {
            this.scheduleExistingBsnSearch(bsn);
        },

        'formData.birthdate'() {
            this.scheduleExistingBsnSearch(this.formData.identification_number);
        },
    },

    methods: {
        async dismissStalePatientDuplicate() {
            try {
                await axios.post(`/calendar/appointment/patient-duplicates/${this.patientDuplicateId}/dismiss`);
            } catch {
                return;
            }

            await DialogManager.alert(i18n.t('calendar.duplicate-patient.no-longer-matching'));
        },

        scheduleExistingBsnSearch(bsn) {
            this.clearExistingBsnSearch();
            this.existingBsnPatient = null;
            this.existingBsnSearchedFor = null;

            if (!this.shouldCheckExistingBsn(bsn) || this.isFillingFromPersonLookup) {
                return;
            }

            this.existingBsnSearchDebounce = setTimeout(() => {
                this.existingBsnSearchDebounce = null;
                this.searchExistingBsnPatient(String(bsn));
            }, EXISTING_BSN_SEARCH_DEBOUNCE_MS);
        },

        clearExistingBsnSearch() {
            clearTimeout(this.existingBsnSearchDebounce);
            this.existingBsnSearchDebounce = null;
            this.isSearchingExistingBsnPatient = false;

            if (this.existingBsnSearchSource) {
                this.existingBsnSearchSource.cancel();
                this.existingBsnSearchSource = null;
            }
        },

        async searchExistingBsnPatient(bsn) {
            const source = axios.CancelToken.source();
            const birthdate = this.existingPatientBirthdate();
            const searchKey = this.existingPatientSearchKey(bsn, birthdate);
            this.existingBsnSearchSource = source;
            this.isSearchingExistingBsnPatient = true;

            try {
                const response = await axios.post(this.patientMatchUrl, { bsn, birthdate }, {
                    cancelToken: source.token,
                });

                if (this.existingPatientSearchKey(
                    this.formData.identification_number,
                    this.existingPatientBirthdate()
                ) !== searchKey) {
                    return;
                }

                this.existingBsnPatient = response.data.data;
                this.existingBsnSearchedFor = searchKey;
            } catch (error) {
                if (!axios.isCancel(error)) {
                    this.existingBsnPatient = null;
                }
            } finally {
                if (this.existingBsnSearchSource === source) {
                    this.existingBsnSearchSource = null;
                    this.isSearchingExistingBsnPatient = false;
                }
            }
        },

        selectExistingBsnPatient() {
            ModalManager.closeModal(this.existingBsnPatient);
        },

        openExistingBsnPatient() {
            if (!this.modalContext) {
                window.location.assign(this.existingBsnPatientEditUrl);
                return;
            }

            const url = updateQueryStringParameter(
                updateQueryStringParameter(this.existingBsnPatientEditUrl, 'sync_session', 'false'),
                'select_after_save',
                '1'
            );

            ModalManager.openModal(url, undefined, undefined, 'full').then(updatedPatient => {
                if (updatedPatient?.id) {
                    ModalManager.closeModal(updatedPatient);
                }
            });
        },

        existingPatientBirthdate() {
            return this.formData.birthdate ? String(this.formData.birthdate).substring(0, 10) : null;
        },

        existingPatientSearchKey(bsn, birthdate) {
            return `${String(bsn || '')}|${birthdate || ''}`;
        },

        shouldCheckExistingBsn(bsn) {
            const normalizedBsn = String(bsn || '');

            return this.personLookupEnabled &&
                this.isNewPatient &&
                normalizedBsn !== DUPLICATE_BSN_EXEMPT &&
                COMPLETE_BSN_PATTERN.test(normalizedBsn);
        },

        async afterSubmit(event) {
            const bsn = String(this.formData.identification_number || '');

            if (!event || !this.shouldCheckExistingBsn(bsn)) {
                return;
            }

            event.preventDefault();

            // The watcher already looked this BSN up while typing, so only search when that result is missing
            const birthdate = this.existingPatientBirthdate();

            if (this.existingBsnSearchedFor !== this.existingPatientSearchKey(bsn, birthdate)) {
                this.existingBsnPatient = null;
                await this.searchExistingBsnPatient(bsn);
            }

            this.resubmitForm(event);
        },

        showMatchedPatientsModal() {
            ModalManager.openModal(
                MatchedPatients,
                {
                    sourcePatient: this.formData,
                    matchedPatients: this.matchedPatients,
                    mergePatientsUrl: this.mergePatientsUrl,
                },
                undefined,
                this.matchedPatients.length === 1 ? 'large' : 'full',
                false
            );
        },

        resetFinancialRelationNumber() {
            this.formData.patient_financial.financial_relation_number = this.initialFinancialRelationNumber;
        },

        onLookupFill({ lookupData, policies }) {
            this.isFillingFromPersonLookup = true;
            this.personLookupAutofilledFields = {};
            this.personLookupAutofilledValues = {};
            this.personLookupAutofilledPreviousValues = {};

            this.applyPersonLookupData(lookupData);

            if (policies.length > 0) {
                this.storePersonLookupPolicyValues();
            }

            this.$nextTick(() => {
                this.removeEmptyPersonLookupAutofilledFields();
                this.isFillingFromPersonLookup = false;
                this.scheduleExistingBsnSearch(this.formData.identification_number);
            });
        },

        onLookupReset() {
            this.isFillingFromPersonLookup = true;
            this.restorePersonLookupAutofilledFields();

            this.$nextTick(() => {
                this.isFillingFromPersonLookup = false;
                this.scheduleExistingBsnSearch(this.formData.identification_number);
            });
        },

        onPoliciesChanged({ policies, isPoliciesRefreshActive, hasAvailableAdditionalReimbursement }) {
            this.personLookupPolicies = policies;
            this.isPersonLookupPoliciesRefreshActive = isPoliciesRefreshActive;
            this.hasAvailableAdditionalReimbursement = hasAvailableAdditionalReimbursement;
        },

        onLookupMatchFound(patient) {
            this.personLookupMatchedPatient = patient;
            this.isPersonLookupMatchBlocking = true;
        },

        onLookupMatchCleared() {
            this.personLookupMatchedPatient = null;
            this.isPersonLookupMatchBlocking = false;
        },

        onLookupMatchRejected() {
            this.personLookupMatchedPatient = null;
            this.isPersonLookupMatchBlocking = false;
        },

        onLookupPatientUpdated(patient) {
            this.personLookupMatchedPatient = patient;
        },

        selectGeneralTab() {
            if (this.$refs.tabs) {
                this.$refs.tabs.select('general');
            }
        },

        requestPersonalData(event) {
            if (!this.enableRequestPersonalData) {
                return DialogManager.alert(this.$t('patient.sbvz.request-personal-data.required-fields-missing'));
            }

            this.selectGeneralTab();

            let url = event.currentTarget.dataset.url;
            this.formData.identification_number && (url = updateQueryStringParameter(url, 'identification_number', this.formData.identification_number));
            this.formData.name.lastname && (url = updateQueryStringParameter(url, 'name[lastname]', this.formData.name.lastname));
            this.formData.name.lastname_prefix && (url = updateQueryStringParameter(url, 'name[lastname_prefix]', this.formData.name.lastname_prefix));
            this.formData.name.initials && (url = updateQueryStringParameter(url, 'name[initials]', this.formData.name.initials));
            this.formData.name.firstname && (url = updateQueryStringParameter(url, 'name[firstname]', this.formData.name.firstname));
            this.formData.name.call_name && (url = updateQueryStringParameter(url, 'name[call_name]', this.formData.name.call_name));
            this.formData.address.street && (url = updateQueryStringParameter(url, 'address[street]', this.formData.address.street));
            this.formData.address.house_number && (url = updateQueryStringParameter(url, 'address[house_number]', this.formData.address.house_number));
            this.formData.address.house_number_suffix && (url = updateQueryStringParameter(url, 'address[house_number_suffix]', this.formData.address.house_number_suffix));
            this.formData.address.postal_code && (url = updateQueryStringParameter(url, 'address[postal_code]', this.formData.address.postal_code));
            this.formData.address.city && (url = updateQueryStringParameter(url, 'address[city]', this.formData.address.city));
            this.formData.birthdate && (url = updateQueryStringParameter(url, 'birthdate', this.formData.birthdate));
            this.formData.gender && (url = updateQueryStringParameter(url, 'gender', this.formData.gender));
            this.formData.deceased && (url = updateQueryStringParameter(url, 'deceased', this.formData.deceased));
            this.formData.deceased_date && (url = updateQueryStringParameter(url, 'deceased_date', this.formData.deceased_date));

            return ModalManager.openModal(url)
                .then((data) => {
                    if (data !== null && typeof data === 'object') {
                        this.formData = merge(this.formData, data);
                        this.$nextTick(() => {
                            this.formData.cov_check = true;
                        });
                    }
                });
        },

        verifyBsn(event) {
            if (!this.enableBsnVerification) {
                return DialogManager.alert(this.$t('patient.sbvz.bsn-verification.required-fields-missing'));
            }

            const url = event.currentTarget.dataset.url;
            const data = {
                bsn: this.formData.identification_number,
                lastname: this.formData.name.lastname,
                birthdate: this.formData.birthdate,
                gender: this.formData.gender,
            };

            return this.verifyData(url, data)
                .then((valid) => {
                    if (valid) {
                        return DialogManager.alert(this.$t('patient.sbvz.bsn-verification.valid'));
                    }

                    return DialogManager.alert(this.$t('patient.sbvz.bsn-verification.invalid'));
                })
                .catch(() => {
                    return DialogManager.alert(this.$t('base.errors.general'));
                });
        },

        verifyIdentificationData(event) {
            if (!this.enableIdentificationDataVerification) {
                return DialogManager.alert(this.$t('patient.sbvz.wid-check.required-fields-missing'));
            }

            const url = event.currentTarget.dataset.url;
            const data = {
                bsn: this.formData.identification_number,
                document_type: this.formData.identification_data.document_type,
                document_number: this.formData.identification_data.document_number,
            };

            return this.verifyData(url, data)
                .then((valid) => {
                    if (valid) {
                        return DialogManager.alert(this.$t('patient.sbvz.wid-check.valid'));
                    }

                    return DialogManager.alert(this.$t('patient.sbvz.wid-check.invalid'));
                })
                .catch(() => {
                    return DialogManager.alert(this.$t('base.errors.general'));
                });
        },

        verifyData(url, data) {
            if (this.$options.previousSource) {
                this.$options.previousSource.cancel();
                this.$options.previousSource = null;
            }

            this.$options.previousSource = axios.CancelToken.source();

            return axios
                .post(url, data, {
                    cancelToken: this.$options.previousSource.token,
                })
                .then(response => {
                    return response.data.data.result === 'G';
                });
        },
    },

    previousSource: null,
};
</script>
